import { useState } from "react";
import { BarChart3, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

const endsLabel = (poll) => {
  if (poll.closed) return "Umfrage beendet";
  if (!poll.ends_at) return "Läuft unbegrenzt";
  const h = Math.max(1, Math.round((new Date(poll.ends_at) - Date.now()) / 3600000));
  return h >= 24 ? `Noch ${Math.round(h / 24)} Tag(e)` : `Noch ${h} Std.`;
};

const Option = ({ o, poll, showResults, busy, onVote }) => {
  const pct = poll.total ? Math.round((o.votes / poll.total) * 100) : 0;
  const mine = poll.my_vote === o.id;
  if (!showResults) return (
    <button onClick={() => onVote(o.id)} disabled={busy} data-testid={`poll-option-${o.id}`}
      className="w-full min-h-11 px-4 py-2 rounded-full border-2 border-rose-200 text-rose-700 font-semibold text-sm text-left hover:bg-rose-50 active:scale-[0.99] transition-[background-color,transform] disabled:opacity-60 break-words">{o.text}</button>
  );
  return (
    <div className="relative min-h-11 rounded-full bg-rose-50 overflow-hidden" data-testid={`poll-result-${o.id}`}>
      <div className={`absolute inset-y-0 left-0 ${mine ? "bg-rose-400" : "bg-rose-200"} transition-[width] duration-700`} style={{ width: `${pct}%` }} />
      <div className={`relative flex items-center justify-between gap-2 px-4 py-2 text-sm font-semibold ${mine && pct > 50 ? "text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.25)]" : "text-slate-800"}`}>
        <span className="flex items-center gap-1.5 break-words min-w-0">{mine && <CheckCircle2 className="w-4 h-4 shrink-0" />}{o.text}</span>
        <span data-testid={`poll-pct-${o.id}`}>{pct}%</span>
      </div>
    </div>
  );
};

export function PollBlock({ postId, poll: initial, onChange }) {
  const [poll, setPoll] = useState(initial);
  const [busy, setBusy] = useState(false);
  const vote = async (optionId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/feed/posts/${postId}/vote`, { option_id: optionId });
      setPoll(data.poll); onChange?.(data.poll);
      toast.success(data.points_earned ? `Abgestimmt! +${data.points_earned} Punkt${data.points_earned > 1 ? "e" : ""} ✨` : "Danke fürs Abstimmen!");
      if (data.points_earned) window.dispatchEvent(new Event("points-changed"));
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const showResults = !!poll.my_vote || poll.closed;
  return (
    <div className="space-y-2" data-testid={`poll-${postId}`}>
      {poll.options.map((o) => <Option key={o.id} o={o} poll={poll} showResults={showResults} busy={busy} onVote={vote} />)}
      <p className="text-xs text-slate-400 flex items-center gap-1.5 pt-1" data-testid={`poll-meta-${postId}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BarChart3 className="w-3.5 h-3.5" />} {poll.total} Stimme{poll.total === 1 ? "" : "n"} · {endsLabel(poll)}
      </p>
    </div>
  );
}
